<template>
  <expansion-panel
    key="expansion-panel-offer-data"
    :hideDivider="panelHideDivider"
    :readonly="panelReadonly"
    :title="$t('offer-manager.create.step-offer-data.title')">
    <template v-slot:panel-content>
      <m-form-row>
        <template v-slot:custom-label>
          <div class="d-flex align-center text-left">
            <span>{{ $t('offer-manager.create.step-offer-data.lbl-attended-by') }}</span>
            <div data-cy="offerDataInfoAttended" @click="openCloseInfoDialog('attended-by-dialog')">
              <v-icon class="fa-light fa-circle-info ml-1 px-2 click" color="tertiary" dense />
            </div>
          </div>
        </template>

        <template v-slot:default>
          <m-text-field v-model="attendedBy" data-cy="offerAttendedBy" readonly />
        </template>
      </m-form-row>

      <m-form-row>
        <template v-slot:custom-label>{{ $t('offer-manager.create.step-offer-data.lbl-offer-type') }}*</template>

        <template v-slot:default>
          <m-text-field v-if="offerId" v-model="offerTypeText" readonly />
          <m-select
            v-else
            v-model="offerTypeSelected"
            :items="offerTypesOptions"
            :placeholder="$t('offer-manager.create.step-offer-data.select-offer-type')"
            :rules="offerTypeRules"
            data-cy="offerType" />
        </template>
      </m-form-row>

      <m-form-row v-if="createDateDisplay" :label="$t('offer-manager.create.step-offer-data.lbl-date-of-creation')">
        <m-date-picker
          v-model="createDate"
          :disabled="true"
          :key="datePickerRefreshKey"
          :locale="locale"
          :min="createDateRules.min"
          :max="createDateRules.max"
          :placeholder="createDatePlaceholder"
          data-cy="offerCreationDate" />
      </m-form-row>

      <m-form-row v-if="isSubagentOffer" :label="$t('offer-manager.create.step-offer-data.lbl-subagent-name')">
        <mapit-input
          :disableAttach="disableAttach"
          :disabled="disabled"
          :listItems="subagentSuggestions"
          :maxlength="100"
          :placeholder="$t('offer-manager.create.step-offer-data.lbl-subagent-name')"
          :type="InputTypes.TEXT_FIELD_WITH_SUGGESTIONS"
          :value="subagentName"
          uid="offerSubagentName"
          @changeValue="value => updateOffer('setSubagentName', toCapitalize(value))" />
      </m-form-row>

      <mapit-dialog :dialog="dialog" @close="openCloseInfoDialog">
        <template v-slot:header-text>
          <span v-if="infoVariant">{{ $t(`offer-manager.create.step-offer-data.${infoVariant}.title`) }}</span>
        </template>

        <template v-slot:body>
          <div
            v-if="infoVariant"
            class="mb-6"
            v-html="$t(`offer-manager.create.step-offer-data.${infoVariant}.description`)"></div>
        </template>
      </mapit-dialog>
    </template>
  </expansion-panel>
</template>

<script lang="ts">
import {computed, defineComponent, nextTick, ref, watch} from '@vue/composition-api'
import {DateTime} from 'luxon'

import MapitDialog from '@/components/dialog/MapitDialog.vue'
import ExpansionPanel from '@/components/general/ExpansionPanel.vue'
import MapitInput from '@/components/general/MapitInput.vue'
import {attendedByRules, offerTypeRules} from '@/helpers/offer-manager-rules'
import {OfferType, offerTypes} from '@/types/offers'
import {InputTypes} from '@/utils/constants'
import {DATE_FORMAT_ISO, getDatePattern} from '@/utils/date-helper'
import {mapState} from '@/utils/map-store'
import {toCapitalize} from '@/utils/utils'

export default defineComponent({
  name: 'OfferData',
  components: {ExpansionPanel, MapitInput, MapitDialog},
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    panelReadonly: {
      type: Boolean,
      default: false,
    },
    panelHideDivider: {
      type: Boolean,
      default: false,
    },
    disableAttach: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, {root, emit}) {
    const infoVariant = ref('')
    const dialog = ref(false)
    const datePickerRefreshKey = ref(0)
    const offerTypeSelected = ref('')

    const {permissions} = mapState(root.$store.state)
    const {
      getLocale: locale,
      getAttendedBy: attendedBy,
      getSubagentName: subagentName,
      getSubagentSuggestions: subagentSuggestions,
      getOfferId: offerId,
      getOfferType: offerType,
      isOfferLoading,
    } = mapState(root.$store.getters)

    const createDate = computed({
      // TODO: replace on change vue3
      get: (): string | undefined => root.$store.getters.getCreateDate,
      set: (value: string | undefined) => root.$store.commit('SET_CREATE_DATE', value),
    })

    const createDateDisplay = computed(() => {
      return (!offerId.value && isSubagentOffer.value) || offerId.value
    })

    const createDateRules = computed(() => {
      const today = DateTime.now()

      return {
        max: today.plus({years: 1}).toFormat(DATE_FORMAT_ISO),
        min: '1900-01-01',
      }
    })

    const createDatePlaceholder = computed(() => {
      return getDatePattern(locale.value)
    })

    const isSubagentOffer = computed(() => {
      return (
        permissions.value.supportsSubagent &&
        (offerTypeSelected.value === OfferType.subagent || offerType.value === OfferType.subagent)
      )
    })

    const offerTypeText = computed(() => {
      return getOfferTypeText(offerType.value)
    })

    const offerTypesOptions = computed(() => {
      return offerTypes
        .map(offerType => {
          if (offerType === OfferType.subagent && permissions.value.supportsSubagent === false) {
            return
          }

          return {
            text: getOfferTypeText(offerType),
            value: offerType,
          }
        })
        .filter(option => option)
    })

    watch(createDate, () => {
      nextTick(() => emit('validateForm')) // nextTick fix error validate async on use "picker"
    })

    watch(isOfferLoading, () => {
      datePickerRefreshKey.value = datePickerRefreshKey.value + 1
    })

    watch(offerTypeSelected, () => {
      root.$store.dispatch('setOfferType', offerTypeSelected.value)
    })

    function updateOffer(action: string, value: any) {
      root.$store.dispatch(action, value)
      emit('validateForm')
    }

    function openCloseInfoDialog(variant: string) {
      dialog.value = !dialog.value
      infoVariant.value = variant
    }

    function getOfferTypeText(offerType: string) {
      return root.$t(`offer-manager.create.step-offer-data.offer-types.${offerType}`)
    }

    return {
      attendedBy,
      attendedByRules,
      createDate,
      createDateDisplay,
      createDatePlaceholder,
      createDateRules,
      datePickerRefreshKey,
      dialog,
      infoVariant,
      InputTypes,
      isSubagentOffer,
      locale,
      offerId,
      offerType,
      offerTypeRules,
      offerTypeSelected,
      offerTypesOptions,
      offerTypeText,
      openCloseInfoDialog,
      permissions,
      subagentName,
      subagentSuggestions,
      toCapitalize,
      updateOffer,
    }
  },
})
</script>
